import { useCallback, useEffect, useRef, useState } from "react";
import {
  type SpTargetCategoryCandidate,
  searchSpTargetCategories,
} from "@/lib/campaign-creation-api";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

/** Debounced, paginated Sponsored Products category search for strategy editors. */
export function useSpCategorySearch(
  shopId: string,
  { enabled = true, onError }: { enabled?: boolean; onError?: () => void } = {},
) {
  const [query, setQueryState] = useState("");
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState<SpTargetCategoryCandidate[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const setQuery = useCallback((next: string) => {
    setQueryState(next);
    setPage(1);
    setCategories([]);
    setHasMore(false);
    setFailed(false);
  }, []);
  const loadMore = useCallback(() => {
    // Disable "load more" through the debounce window, not just the request.
    setLoading(true);
    setPage((current) => current + 1);
  }, []);

  useEffect(() => {
    const search = query.trim();
    if (!enabled || search.length < MIN_QUERY_LENGTH) {
      setCategories([]);
      setHasMore(false);
      setLoading(false);
      setFailed(false);
      return;
    }
    let stale = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setFailed(false);
      void (
        page === 1
          ? searchSpTargetCategories(shopId, search)
          : searchSpTargetCategories(shopId, search, page)
      )
        .then((response) => {
          if (stale) return;
          setCategories((current) => (page === 1 ? response.data : [...current, ...response.data]));
          setHasMore(response.pagination.hasMore);
        })
        .catch(() => {
          if (stale) return;
          setHasMore(false);
          setFailed(true);
          onErrorRef.current?.();
        })
        .finally(() => {
          if (!stale) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      stale = true;
      window.clearTimeout(timer);
    };
  }, [enabled, page, query, shopId]);

  return {
    query,
    setQuery,
    categories,
    hasMore,
    loading,
    failed,
    loadMore,
  };
}
